import React, { createContext, useContext, useEffect, useState } from 'react';

interface DarkModeContextProps {
  isDark: boolean;
  toggleDarkMode: () => void;
}

// Create dark mode context with undefined default value
const DarkModeContext = createContext<DarkModeContextProps | undefined>(undefined);

/**
 * DarkModeProvider: Manages dark mode state and provides dark mode context to the application
 * Wraps the entire app to ensure dark mode state is available everywhere
 * @param children - The child components to wrap
 * @returns The DarkModeProvider component
 */
export const DarkModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDark));
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleDarkMode = () => {
    setIsDark(!isDark);
  };

  return (
    <DarkModeContext.Provider value={{ isDark, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

/**
 * Custom hook to access dark mode context
 * Throws error if used outside of DarkModeProvider
 */
export const useDarkModeContext = () => {
  const ctx = useContext(DarkModeContext);
  if (ctx === undefined) {
    throw new Error('useDarkModeContext must be used within a DarkModeProvider');
  }
  return ctx;
};
